export type ThemePreference = 'system' | 'dark' | 'moderate' | 'light';

export type ResolvedThemeMode = 'dark' | 'moderate' | 'light';

export type ColorSchemePreference =
  | 'classic'
  | 'midnight'
  | 'aurora'
  | 'sunset'
  | 'ocean'
  | 'neon'
  | 'ember'
  | 'forest'
  | 'violet'
  | 'slate'
  | 'rose'
  | 'gold'
  | 'pastel-mint'
  | 'pastel-lavender'
  | 'pastel-peach'
  | 'pastel-sky'
  | 'pastel-blush'
  | 'pastel-sand';

export type IconSetPreference = 'standard' | 'neon-mono' | 'neon-multi';

/** Custom theme shell brightness — 0 (dim) through 100 (bright). */
export type CustomBrightness = number;

/** @deprecated Stored only for migration from prior soft / balanced / bright presets. */
export type ModerateBrightnessPreference = 'soft' | 'balanced' | 'bright';

export const CUSTOM_BRIGHTNESS_MIN = 0;
export const CUSTOM_BRIGHTNESS_MAX = 100;
export const CUSTOM_BRIGHTNESS_DEFAULT = 0;

/** Neon icon halo strength — soft is the default (least wash). */
export type NeonGlowPreference = 'soft' | 'medium' | 'strong';

export interface DevicePreferences {
  theme: ThemePreference;
  colorScheme: ColorSchemePreference;
  iconSet: IconSetPreference;
  /** 0–100 slider value when theme is Custom (stored as `moderate`). */
  customBrightness: CustomBrightness;
  neonGlow: NeonGlowPreference;
  reduceMotion: boolean;
  compactMode: boolean;
  /** Desktop dashboard left nav — false = collapsed/hidden */
  sidebarNavExpanded: boolean;
  /** Settings detail column — false = hidden */
  rightPanelOpen: boolean;
  /** Desktop workspace right rail width in pixels (0 = collapsed). */
  rightRailWidth: number;
  cookieConsent: boolean;
  doNotSell: boolean;
}

export const RIGHT_RAIL_WIDTH_MIN = 0;
export const RIGHT_RAIL_WIDTH_MAX = 560;
export const RIGHT_RAIL_WIDTH_DEFAULT = 320;

export const PREFERENCES_KEY = 'quickcam.dashboard.preferences.v1';

export const COLOR_SCHEME_OPTIONS: {
  value: ColorSchemePreference;
  label: string;
  description: string;
}[] = [
  {
    value: 'classic',
    label: 'Classic Blue',
    description: 'Default QuickCam blue and violet accents.',
  },
  {
    value: 'midnight',
    label: 'Midnight',
    description: 'Deep indigo surfaces with cool highlights.',
  },
  {
    value: 'aurora',
    label: 'Aurora',
    description: 'Teal and emerald accents on a dark canvas.',
  },
  {
    value: 'sunset',
    label: 'Sunset',
    description: 'Warm coral and amber accents.',
  },
  {
    value: 'ocean',
    label: 'Ocean',
    description: 'Cyan and sea-blue accents.',
  },
  {
    value: 'neon',
    label: 'Neon Pulse',
    description: 'Electric magenta and lime accents.',
  },
  {
    value: 'ember',
    label: 'Ember',
    description: 'Charcoal surfaces with ember orange accents.',
  },
  {
    value: 'forest',
    label: 'Forest',
    description: 'Deep greens with moss highlights.',
  },
  {
    value: 'violet',
    label: 'Violet',
    description: 'Rich violet and orchid accents.',
  },
  {
    value: 'slate',
    label: 'Slate',
    description: 'Cool steel blues and silver accents.',
  },
  {
    value: 'rose',
    label: 'Rose',
    description: 'Soft rose and blush accents.',
  },
  {
    value: 'gold',
    label: 'Gold',
    description: 'Champagne gold on a dark bronze canvas.',
  },
  {
    value: 'pastel-mint',
    label: 'Pastel Mint',
    description: 'Soft mint and sage accents.',
  },
  {
    value: 'pastel-lavender',
    label: 'Pastel Lavender',
    description: 'Muted lavender and periwinkle accents.',
  },
  {
    value: 'pastel-peach',
    label: 'Pastel Peach',
    description: 'Gentle peach and apricot accents.',
  },
  {
    value: 'pastel-sky',
    label: 'Pastel Sky',
    description: 'Airy sky blue and powder accents.',
  },
  {
    value: 'pastel-blush',
    label: 'Pastel Blush',
    description: 'Soft blush pink and rose accents.',
  },
  {
    value: 'pastel-sand',
    label: 'Pastel Sand',
    description: 'Warm sand and cream accents.',
  },
];

export const ICON_SET_OPTIONS: {
  value: IconSetPreference;
  label: string;
  description: string;
}[] = [
  {
    value: 'standard',
    label: 'Standard',
    description: 'Clean Material icons without glow.',
  },
  {
    value: 'neon-mono',
    label: 'Neon Monochrome',
    description: 'One neon accent color on every icon, with a crisp glow.',
  },
  {
    value: 'neon-multi',
    label: 'Neon Multi-color',
    description: 'Each icon in its own neon color, with a soft halo.',
  },
];

/** Per-nav neon colors for Neon Multi-color (must match dashboard-preferences.scss). */
export const NEON_MULTI_NAV_COLORS: Record<string, string> = {
  home: '#2a9dff',
  share: '#ff2bd6',
  connections: '#39ff14',
  admin: '#ffc857',
  'admin-dashboard': '#ffc857',
  'admin-creator-upgrades': '#ffc857',
  'admin-sizzle': '#ffc857',
  'admin-credits': '#ffc857',
  credits: '#18c3e6',
  'creator-tools': '#ff7a59',
  'creator-overview': '#ff7a59',
  'creator-subscriptions': '#ff7a59',
  'creator-purchase-history': '#ff7a59',
  commissions: '#b388ff',
  leaderboard: '#56f0a5',
  announcements: '#ff5c8a',
  downloads: '#7c8cff',
  help: '#20e5f5',
  faqs: '#20e5f5',
  tutorials: '#20e5f5',
  notifications: '#ffd60a',
  settings: '#9d5cff',
  'settings-general': '#2a9dff',
  'settings-profile': '#39ff14',
  'settings-notifications': '#ffd60a',
  'settings-appearance': '#20e5f5',
  'settings-password': '#ffc857',
  'settings-privacy': '#7c8cff',
  'settings-plan': '#18c3e6',
  'settings-danger': '#ff6b6b',
  logout: '#ff6b6b',
  referrals: '#39ff14',
  referrers: '#7cffb2',
  subscribers: '#2a9dff',
  favorites: '#ff5c8a',
  following: '#ffc857',
  followers: '#18c3e6',
};

/** Preview swatch colors for Neon Multi-color cards (home / share / people / star). */
export const NEON_MULTI_PREVIEW_COLORS = [
  '#2a9dff',
  '#ff2bd6',
  '#39ff14',
  '#ffc857',
] as const;

export const NEON_GLOW_OPTIONS: {
  value: NeonGlowPreference;
  label: string;
  description: string;
}[] = [
  {
    value: 'soft',
    label: 'Soft',
    description: 'Tight halo — sharp icon, gentle glow.',
  },
  {
    value: 'medium',
    label: 'Medium',
    description: 'Noticeable glow without washing out the glyph.',
  },
  {
    value: 'strong',
    label: 'Strong',
    description: 'Bigger neon bloom behind a sharp icon.',
  },
];

const DEFAULT_PREFERENCES: DevicePreferences = {
  theme: 'dark',
  colorScheme: 'classic',
  iconSet: 'standard',
  customBrightness: CUSTOM_BRIGHTNESS_DEFAULT,
  neonGlow: 'soft',
  reduceMotion: false,
  compactMode: false,
  sidebarNavExpanded: true,
  rightPanelOpen: true,
  rightRailWidth: RIGHT_RAIL_WIDTH_DEFAULT,
  cookieConsent: false,
  doNotSell: false,
};

export function clampRightRailWidth(value: number): number {
  if (!Number.isFinite(value)) return RIGHT_RAIL_WIDTH_DEFAULT;
  return Math.min(
    RIGHT_RAIL_WIDTH_MAX,
    Math.max(RIGHT_RAIL_WIDTH_MIN, Math.round(value)),
  );
}

function isTheme(value: unknown): value is ThemePreference {
  return (
    value === 'system' ||
    value === 'dark' ||
    value === 'moderate' ||
    value === 'light'
  );
}

function isColorScheme(value: unknown): value is ColorSchemePreference {
  return COLOR_SCHEME_OPTIONS.some(option => option.value === value);
}

function normalizeIconSet(value: unknown): IconSetPreference | null {
  if (value === 'standard' || value === 'neon-mono' || value === 'neon-multi') {
    return value;
  }
  // Prior single "neon" preference maps to monochrome multi-hue set.
  if (value === 'neon') return 'neon-mono';
  return null;
}

function isModerateBrightness(
  value: unknown,
): value is ModerateBrightnessPreference {
  return value === 'soft' || value === 'balanced' || value === 'bright';
}

function migrateModerateBrightness(value: unknown): CustomBrightness {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.min(
      CUSTOM_BRIGHTNESS_MAX,
      Math.max(CUSTOM_BRIGHTNESS_MIN, Math.round(value)),
    );
  }
  if (isModerateBrightness(value)) {
    if (value === 'balanced') return 50;
    if (value === 'bright') return 100;
    return CUSTOM_BRIGHTNESS_DEFAULT;
  }
  return CUSTOM_BRIGHTNESS_DEFAULT;
}

const CUSTOM_SHELL_DIM = {
  bg: '#b4c0d0',
  surface: '#c5cfdb',
  surfaceStrong: '#aebaca',
  headerRgb: [197, 207, 219] as const,
  footer: '#c5cfdb',
};

const CUSTOM_SHELL_BRIGHT = {
  bg: '#d8e0ea',
  surface: '#e6ebf2',
  surfaceStrong: '#cfd8e4',
  headerRgb: [230, 235, 242] as const,
  footer: '#e6ebf2',
};

const CUSTOM_SHELL_CSS_VARS = [
  '--qc-shell-bg',
  '--qc-shell-surface',
  '--qc-shell-surface-strong',
  '--qc-shell-header',
  '--qc-shell-footer',
] as const;

function lerpChannel(from: number, to: number, t: number): number {
  return Math.round(from + (to - from) * t);
}

function lerpHex(from: string, to: string, t: number): string {
  const parse = (hex: string) => {
    const normalized = hex.replace('#', '');
    return [
      parseInt(normalized.slice(0, 2), 16),
      parseInt(normalized.slice(2, 4), 16),
      parseInt(normalized.slice(4, 6), 16),
    ] as const;
  };
  const [r1, g1, b1] = parse(from);
  const [r2, g2, b2] = parse(to);
  const r = lerpChannel(r1, r2, t);
  const g = lerpChannel(g1, g2, t);
  const b = lerpChannel(b1, b2, t);
  return `#${[r, g, b]
    .map(channel => channel.toString(16).padStart(2, '0'))
    .join('')}`;
}

function customShellCssVars(brightness: CustomBrightness): Record<string, string> {
  const t = Math.min(
    1,
    Math.max(0, brightness / CUSTOM_BRIGHTNESS_MAX),
  );
  const headerRgb = CUSTOM_SHELL_DIM.headerRgb.map((channel, index) =>
    lerpChannel(channel, CUSTOM_SHELL_BRIGHT.headerRgb[index], t),
  );
  return {
    '--qc-shell-bg': lerpHex(CUSTOM_SHELL_DIM.bg, CUSTOM_SHELL_BRIGHT.bg, t),
    '--qc-shell-surface': lerpHex(
      CUSTOM_SHELL_DIM.surface,
      CUSTOM_SHELL_BRIGHT.surface,
      t,
    ),
    '--qc-shell-surface-strong': lerpHex(
      CUSTOM_SHELL_DIM.surfaceStrong,
      CUSTOM_SHELL_BRIGHT.surfaceStrong,
      t,
    ),
    '--qc-shell-header': `rgba(${headerRgb.join(', ')}, 0.96)`,
    '--qc-shell-footer': lerpHex(
      CUSTOM_SHELL_DIM.footer,
      CUSTOM_SHELL_BRIGHT.footer,
      t,
    ),
  };
}

function clearCustomShellCssVars(root: HTMLElement): void {
  CUSTOM_SHELL_CSS_VARS.forEach(name => root.style.removeProperty(name));
}

function applyCustomShellCssVars(
  root: HTMLElement,
  brightness: CustomBrightness,
): void {
  const vars = customShellCssVars(brightness);
  Object.entries(vars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
}

function isNeonGlow(value: unknown): value is NeonGlowPreference {
  return value === 'soft' || value === 'medium' || value === 'strong';
}

export function getDefaultPreferences(): DevicePreferences {
  return {
    ...DEFAULT_PREFERENCES,
    cookieConsent: localStorage.getItem('isCookiesAccepted') === 'true',
    doNotSell: localStorage.getItem('isPersonalInformationSell') === 'true',
  };
}

export function readPreferences(): DevicePreferences {
  const fallback = getDefaultPreferences();
  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    if (!stored) return fallback;
    const parsed = JSON.parse(stored) as Partial<
      DevicePreferences & { moderateBrightness?: unknown }
    >;
    const legacyBrightness =
      parsed.customBrightness ?? parsed.moderateBrightness;
    return {
      ...fallback,
      ...parsed,
      theme: isTheme(parsed.theme) ? parsed.theme : fallback.theme,
      colorScheme: isColorScheme(parsed.colorScheme)
        ? parsed.colorScheme
        : fallback.colorScheme,
      iconSet: normalizeIconSet(parsed.iconSet) || fallback.iconSet,
      customBrightness: migrateModerateBrightness(legacyBrightness),
      neonGlow: isNeonGlow(parsed.neonGlow)
        ? parsed.neonGlow
        : fallback.neonGlow,
      sidebarNavExpanded:
        typeof parsed.sidebarNavExpanded === 'boolean'
          ? parsed.sidebarNavExpanded
          : fallback.sidebarNavExpanded,
      rightPanelOpen:
        typeof parsed.rightPanelOpen === 'boolean'
          ? parsed.rightPanelOpen
          : fallback.rightPanelOpen,
      rightRailWidth:
        typeof parsed.rightRailWidth === 'number'
          ? clampRightRailWidth(parsed.rightRailWidth)
          : fallback.rightRailWidth,
    };
  } catch (_error) {
    return fallback;
  }
}

export function resolveThemeMode(theme: ThemePreference): ResolvedThemeMode {
  if (theme === 'light') return 'light';
  if (theme === 'dark') return 'dark';
  if (theme === 'moderate') return 'moderate';
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

/** Matches cinematic landing tokens — never written to PREFERENCES_KEY. */
export const LANDING_APPEARANCE_SNAPSHOT = {
  theme: 'dark',
  scheme: 'classic',
  icons: 'standard',
  glow: 'soft',
  vars: {
    '--qc-shell-bg': '#020712',
    '--qc-shell-surface': '#071426',
    '--qc-shell-surface-strong': '#091a31',
    '--qc-shell-copy': '#f8f9ff',
    '--qc-shell-muted': '#a9b5ca',
    '--qc-shell-header': 'rgba(2, 7, 18, 0.92)',
    '--qc-shell-footer': '#071426',
    '--qc-accent': '#2a9dff',
    '--qc-accent-2': '#9d5cff',
    '--qc-accent-soft': 'rgba(42, 157, 255, 0.18)',
    '--qc-accent-line': 'rgba(91, 151, 255, 0.28)',
    '--qc-glow': 'rgba(42, 157, 255, 0.45)',
    '--qc-btn-bg': '#3b6ea5',
    '--qc-btn-bg-hover': '#335f8f',
    '--qc-btn-fg': '#f8fafc',
  },
} as const;

/**
 * First-path segments that are dedicated app/public routes, not NewHome
 * (`/:referrerChannelId?`). Keep in sync with `src/app/app.routes.tsx`.
 */
const NON_LANDING_FIRST_SEGMENTS = new Set([
  'u',
  'onboarding',
  'redirect',
  'about-us',
  'privacy-policy',
  'trust-policy',
  'cookie-policy',
  'terms-and-conditions',
  'terms-and-condition',
  'whitepaper',
  'investors',
  'live-leaderboard',
  'quickstart',
  'share-wizard',
  'quick-tour',
  'quick-tour-home',
  'p-calculator-2',
  'verify',
  'unsubscribe',
  'patentsFoot',
]);

let landingAppearanceLocked = false;

export function isCinematicLandingPath(pathname: string): boolean {
  if (!pathname || pathname === '/') return true;
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return true;
  if (NON_LANDING_FIRST_SEGMENTS.has(segments[0])) return false;
  return segments.length === 1;
}

/** Landing plus onboarding — both keep the cinematic snapshot, not stored Appearance. */
export function isLockedAppearancePath(pathname: string): boolean {
  return (
    isCinematicLandingPath(pathname) ||
    pathname === '/onboarding' ||
    pathname.startsWith('/onboarding/')
  );
}

export function isLandingAppearanceLocked(): boolean {
  return landingAppearanceLocked;
}

function clearLandingSnapshotCssVars(root: HTMLElement): void {
  Object.keys(LANDING_APPEARANCE_SNAPSHOT.vars).forEach(name => {
    root.style.removeProperty(name);
  });
}

function applyLandingSnapshot(): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const { theme, scheme, icons, glow, vars } = LANDING_APPEARANCE_SNAPSHOT;

  root.dataset.quickcamSurface = 'landing';
  root.dataset.quickcamTheme = theme;
  root.dataset.quickcamThemePreference = theme;
  root.dataset.quickcamScheme = scheme;
  root.dataset.quickcamIcons = icons;
  root.dataset.quickcamGlow = glow;
  delete root.dataset.quickcamCustomBrightness;
  delete root.dataset.quickcamModerate;
  clearCustomShellCssVars(root);
  Object.entries(vars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
}

export function setLandingAppearanceLock(locked: boolean): void {
  if (locked) {
    landingAppearanceLocked = true;
    applyLandingSnapshot();
    return;
  }

  const pathname =
    typeof window === 'undefined' ? '/' : window.location.pathname;
  // Cinematic unmount must not restore Light prefs when the next route is
  // onboarding (or still landing). That is how a prior user's Appearance
  // leaked onto the next signup in the same browser.
  if (isLockedAppearancePath(pathname)) {
    landingAppearanceLocked = true;
    applyLandingSnapshot();
    return;
  }

  landingAppearanceLocked = false;
  applyPreferences(readPreferences());
}

export function applyPreferences(preferences: DevicePreferences): void {
  if (typeof document === 'undefined') return;

  if (landingAppearanceLocked) {
    applyLandingSnapshot();
    return;
  }

  const root = document.documentElement;
  delete root.dataset.quickcamSurface;
  clearLandingSnapshotCssVars(root);
  const resolvedTheme = resolveThemeMode(preferences.theme);

  root.dataset.quickcamTheme = resolvedTheme;
  root.dataset.quickcamThemePreference = preferences.theme;
  root.dataset.quickcamScheme = preferences.colorScheme;
  root.dataset.quickcamIcons = preferences.iconSet;
  root.dataset.quickcamCustomBrightness = String(preferences.customBrightness);
  delete root.dataset.quickcamModerate;
  root.dataset.quickcamGlow = preferences.neonGlow;

  if (resolvedTheme === 'moderate') {
    applyCustomShellCssVars(root, preferences.customBrightness);
  } else {
    clearCustomShellCssVars(root);
  }
  root.classList.toggle('quickcam-reduce-motion', preferences.reduceMotion);
  root.classList.toggle('quickcam-compact', preferences.compactMode);
  root.dataset.quickcamSidebar = preferences.sidebarNavExpanded
    ? 'expanded'
    : 'collapsed';
  root.dataset.quickcamRightPanel = preferences.rightPanelOpen
    ? 'open'
    : 'closed';
  root.style.setProperty(
    '--qc-right-rail-width',
    `${clampRightRailWidth(preferences.rightRailWidth)}px`,
  );
}

export function toggleSidebarNavExpanded(): boolean {
  const current = readPreferences();
  const sidebarNavExpanded = !current.sidebarNavExpanded;
  persistPreferences({ ...current, sidebarNavExpanded });
  return sidebarNavExpanded;
}

export function toggleRightPanelOpen(): boolean {
  const current = readPreferences();
  const rightPanelOpen = !current.rightPanelOpen;
  persistPreferences({ ...current, rightPanelOpen });
  return rightPanelOpen;
}

export function setRightRailWidth(width: number): number {
  const current = readPreferences();
  const rightRailWidth = clampRightRailWidth(width);
  persistPreferences({ ...current, rightRailWidth });
  return rightRailWidth;
}

export function persistPreferences(preferences: DevicePreferences): void {
  localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  localStorage.setItem(
    'isCookiesAccepted',
    String(preferences.cookieConsent),
  );
  localStorage.setItem(
    'isPersonalInformationSell',
    String(preferences.doNotSell),
  );
  if (typeof document !== 'undefined') {
    document.cookie = `qc_cookie_consent=${preferences.cookieConsent}; Path=/; Max-Age=31536000; SameSite=Lax`;
    document.cookie = `qc_do_not_sell=${preferences.doNotSell}; Path=/; Max-Age=31536000; SameSite=Lax`;
  }
  applyPreferences(preferences);
}

export function bootstrapDashboardPreferences(): () => void {
  const pathname =
    typeof window === 'undefined' ? '/' : window.location.pathname;
  if (isLockedAppearancePath(pathname)) {
    setLandingAppearanceLock(true);
  } else {
    applyPreferences(readPreferences());
  }

  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => undefined;
  }

  const media = window.matchMedia('(prefers-color-scheme: light)');
  const onChange = () => {
    const current = readPreferences();
    if (current.theme === 'system') {
      applyPreferences(current);
    }
  };

  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }

  media.addListener(onChange);
  return () => media.removeListener(onChange);
}
